import { Users } from "lucide-react";
import { SYSTEM_ICON } from "../../../constants/entity-icons";
import { suggestionRefInfoToDisplay } from "../process-flow/core/builder";
import { getAssigneeName } from "../process-flow/core/utils";
import { formatUserLifecycleSuffix, resolveUserLifecycle } from "../../../utils/deleted-entity-utils";
/**
 * Blue: a proposed (pending entity-suggestion) Team/System. Reads the same way
 * on every surface — the panel name, the canvas chip — so a pending proposal is
 * recognisable at a glance. Once the suggestion is approved its ref is rewritten
 * to `{ type: 'entity' }` and the label renders as a normal entity card with no
 * proposed styling.
 */ const PROPOSED_PALETTE = {
    text: "text-info",
    border: "border-info-solid",
    bg: "bg-info-surface",
    avatar: "bg-info-surface-strong text-info-strong text-xs",
    nodeText: "text-info-strong"
};
/**
 * Red: a declined (rejected) entity-suggestion. A rejected suggestion keeps its
 * `{ type: 'suggestion' }` ref in place (the placeholder assignment persists),
 * so it still renders its proposed name — but red instead of blue, signalling
 * the proposal was turned down rather than still pending.
 */ const DECLINED_PALETTE = {
    text: "text-danger",
    border: "border-danger-solid",
    bg: "bg-danger-surface",
    avatar: "bg-danger-surface-strong text-danger-strong text-xs",
    nodeText: "text-danger-strong"
};
/**
 * The single seam every proposed/declined surface uses: red once declined, blue
 * while pending. The blue/red decision lives here once, so the panels and the
 * canvas chip can never drift apart.
 */ export function proposedEntityPalette(isDeclined) {
    return isDeclined ? DECLINED_PALETTE : PROPOSED_PALETTE;
}
/** Panel/sidebar text class for a proposed (pending) label — `text-info`. */ export const PROPOSED_ENTITY_CLASS = PROPOSED_PALETTE.text;
/**
 * Panel/sidebar label text class for a resolvable suggestion ref: red once
 * declined, blue while pending. Thin wrapper over `proposedEntityPalette(...).text`
 * for the many sites that only colour a name.
 */ export function proposedEntityClass(isDeclined) {
    return proposedEntityPalette(isDeclined).text;
}
/**
 * Entity-type icon for a proposed (pending) OrgFunction/System chip, paired with
 * `PROPOSED_ENTITY_CLASS`. The one source every proposed-OrgFunction/System
 * surface reads (reassign-confirm dialog, recording assign-instead card) so the
 * icon stays identical and can't drift into per-call-site conventions.
 */ export function proposedEntityIcon(kind) {
    return kind === "orgFunction" ? Users : SYSTEM_ICON;
}
/**
 * Proposed name + declined flag of a `suggestion`-typed union ref, resolved
 * against the same `SuggestionRefInfo` map the canvas builds via
 * `buildSuggestionInfoMap`. `isDeclined` is true when the suggestion's status is
 * `rejected`. Returns `undefined` for entity refs, missing refs, or an unknown
 * suggestion id (the caller then keeps its neutral pending placeholder).
 *
 * One seam for both surfaces: the canvas placeholder node and these step-detail
 * panels look the ref up in a `Map<id, SuggestionRefInfo>` and derive the
 * display via the shared `suggestionRefInfoToDisplay`. The map is built once per
 * surface (`ProcessFlowStepDetailContent` / `EditableStepDetailContent` for the
 * panels, `ProcessFlowVisualization` / `ProcessFlowEditor` for the canvas) and
 * threaded down, so every lookup here is O(1).
 */ export function suggestionRefDisplay(ref, suggestionInfoById) {
    if (!ref || ref.type !== "suggestion") return undefined;
    const info = suggestionInfoById?.get(ref.id);
    return info ? suggestionRefInfoToDisplay(info) : undefined;
}
/**
 * The one resolver for rendering an `EntityOrSuggestionRef` slot. Every
 * step-detail surface (successor lists, decision-rule rows, helper systems,
 * who-else entries, assignee fields) used to hand-roll the same four-way
 * decision — suggestion → proposed name | pending placeholder, entity →
 * lookup name (+ deleted suffix) | bare-id fallback — and the copies drifted
 * (decision-rule rows lost the deleted suffix). Centralising the decision here
 * means the branch order, the declined rule, and the deleted-suffix convention
 * can't diverge again; call sites only decide how each `kind` looks.
 */ export function resolveEntityRefDisplay(ref, { suggestionInfoById, pendingLabel, getEntity, t }) {
    if (!ref || ref.type === "suggestion") {
        const proposed = suggestionRefDisplay(ref, suggestionInfoById);
        return proposed ? {
            kind: "proposed",
            label: proposed.name,
            isDeclined: proposed.isDeclined
        } : {
            kind: "pending",
            label: pendingLabel
        };
    }
    const entity = getEntity(ref.id);
    if (!entity) return {
        kind: "missing",
        label: ref.id
    };
    const lifecycle = resolveUserLifecycle(entity);
    const name = getAssigneeName({
        assignedTo: entity
    }) || ref.id;
    return {
        kind: "entity",
        label: `${name}${formatUserLifecycleSuffix(lifecycle, t)}`,
        name,
        lifecycle,
        entity,
        isDeleted: lifecycle === "deleted"
    };
}

//# sourceMappingURL=suggestionNames.js.map